import React, { useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { withRouter, Link } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// actions
import { loginUser } from "../../store/actions";
import AuthShell from "./AuthShell";
import AuthField from "./AuthField";

const Login = (props) => {
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: { email: '', password: '', rememberMe: false },
        validationSchema: Yup.object({
            email: Yup.string().email("Please enter a valid email").required("Email is required"),
            password: Yup.string().required("Password is required"),
        }),
        onSubmit: (values, helpers) => {
            dispatch(loginUser(values, props.history));
            // Hand button state off to the Redux `loading` flag below — the saga
            // resets it on both success and failure. Without this, formik leaves
            // isSubmitting=true forever because the saga isn't a promise it awaits.
            helpers.setSubmitting(false);
        }
    });

    const { error, loading } = useSelector(state => ({
        error: state.Login.error,
        loading: state.Login.loading,
    }));

    document.title = "Sign In | BiorGov Admin";

    return (
        <AuthShell title="Sign in" subtitle="Enter your credentials to continue">
            {error && (
                <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{error}</div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); }}>
                <AuthField
                    label="Email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@biorgov.com"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email || ""}
                    error={validation.errors.email}
                    touched={validation.touched.email}
                />

                <div style={{ marginBottom: "1rem" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                        <label htmlFor="password" style={{ fontSize: 13, fontWeight: 500, color: "var(--bl-text-strong)" }}>
                            Password
                        </label>
                        <Link to="/forgot-password" style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>
                            Forgot password?
                        </Link>
                    </div>
                    <div style={{ position: "relative" }}>
                        <input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Your password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password || ""}
                            className="bl-input"
                            style={{
                                paddingRight: 38,
                                borderColor: validation.touched.password && validation.errors.password
                                    ? "rgba(255,93,108,0.6)"
                                    : "var(--bl-line-strong)"
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            style={{
                                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                                background: "transparent", border: 0, color: "var(--bl-text-soft)",
                                cursor: "pointer", padding: 4,
                            }}
                        >
                            <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} style={{ fontSize: 18 }} />
                        </button>
                    </div>
                    {validation.touched.password && validation.errors.password && (
                        <p style={{ marginTop: 4, fontSize: 12, color: "#ff8a98" }}>{validation.errors.password}</p>
                    )}
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1.25rem", cursor: "pointer" }}>
                    <input
                        id="rememberMe"
                        name="rememberMe"
                        type="checkbox"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        checked={validation.values.rememberMe}
                        style={{ accentColor: "var(--bl-accent)" }}
                    />
                    <span style={{ fontSize: 13, color: "var(--bl-text-strong)" }}>Remember me for 90 days</span>
                </label>

                <button
                    type="submit"
                    className="bl-btn-primary"
                    style={{ width: "100%", padding: "0.7rem 1rem" }}
                    disabled={loading}
                >
                    {loading ? "Signing in…" : "Sign in"}
                </button>
            </form>
        </AuthShell>
    );
};

export default withRouter(Login);
